/**
 * Traducción del `{ data, error, response }` de `openapi-fetch` al estilo del
 * cliente de alto nivel: devolver los datos o lanzar.
 *
 * Es un módulo interno; lo consumen `client.ts` y los namespaces de
 * `src/namespaces/`. No se reexporta desde `index.ts`.
 */
import { ApiClientError } from './errors.js';
/** Devuelve los datos o lanza {@link ApiClientError}. */
export function unwrap(result) {
    if (!result.response.ok)
        throw toApiClientError(result.response, result.error);
    if (result.data === undefined) {
        throw new ApiClientError(result.response.status, 'internal', 'La respuesta del servidor no contiene datos.');
    }
    return result.data;
}
/** Variante para respuestas 204 sin cuerpo. */
export function unwrapVoid(result) {
    if (!result.response.ok)
        throw toApiClientError(result.response, result.error);
}
/**
 * Como {@link unwrap}, pero además dice si el recurso se acaba de crear.
 * Las rutas de upsert (`PUT /v1/workouts/{clientId}` y su equivalente de
 * series) responden `201` la primera vez y `200` en los reenvíos.
 */
export function unwrapUpsert(result) {
    return { value: unwrap(result), created: result.response.status === 201 };
}
/**
 * Como {@link unwrap}, pero partiendo de una `Response` en crudo. La usa la
 * subida de imágenes, que compone el `multipart/form-data` a mano y no pasa por
 * `openapi-fetch`.
 */
export async function unwrapResponse(response) {
    const body = await readJsonBody(response);
    if (!response.ok)
        throw toApiClientError(response, body);
    if (body === undefined) {
        throw new ApiClientError(response.status, 'internal', 'La respuesta del servidor no contiene datos.');
    }
    return body;
}
/** Cuerpo JSON de una respuesta, o `undefined` si viene vacío o no es JSON. */
async function readJsonBody(response) {
    try {
        const text = await response.text();
        return text === '' ? undefined : JSON.parse(text);
    }
    catch {
        // Un proxy puede colar un HTML donde el contrato promete JSON, y un 413 lo
        // corta muchas veces antes de llegar a la aplicación.
        return undefined;
    }
}
/** Convierte una respuesta de error en {@link ApiClientError}. */
export function toApiClientError(response, error) {
    const status = response.status;
    const retryAfterSeconds = parseRetryAfter(response.headers.get('Retry-After'));
    const options = retryAfterSeconds === undefined ? undefined : { retryAfterSeconds };
    if (isApiErrorBody(error)) {
        return new ApiClientError(status, error.code, error.message, error.details, options);
    }
    const message = typeof error === 'string' && error.trim() !== ''
        ? error
        : response.statusText || `La petición ha fallado con estado ${status}.`;
    return new ApiClientError(status, fallbackCode(status), message, undefined, options);
}
/**
 * Lee `Retry-After`: segundos enteros (lo que manda la API) o una fecha HTTP
 * (lo que podría mandar un proxy). `undefined` si falta o no se entiende.
 */
export function parseRetryAfter(value, now = Date.now()) {
    if (value === null)
        return undefined;
    const trimmed = value.trim();
    if (trimmed === '')
        return undefined;
    if (/^\d+$/.test(trimmed))
        return Number(trimmed);
    const at = Date.parse(trimmed);
    if (Number.isNaN(at))
        return undefined;
    return Math.max(0, Math.ceil((at - now) / 1000));
}
function isApiErrorBody(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const candidate = value;
    return typeof candidate['code'] === 'string' && typeof candidate['message'] === 'string';
}
/** Código de repuesto cuando el cuerpo del error no sigue el contrato. */
function fallbackCode(status) {
    if (status === 400)
        return 'validation_error';
    if (status === 401)
        return 'unauthorized';
    if (status === 403)
        return 'forbidden';
    if (status === 404)
        return 'not_found';
    // Un proxy o el propio servidor pueden cortar una subida demasiado grande sin
    // llegar a componer el cuerpo del contrato.
    if (status === 413)
        return 'payload_too_large';
    // Una fuente externa (hoy solo Open Food Facts) no ha respondido: la operación
    // puede reintentarse más tarde, no es un fallo nuestro ni del cliente.
    if (status === 502)
        return 'upstream_unavailable';
    return 'internal';
}
//# sourceMappingURL=http.js.map